import type { ApiKeyCreatedDTO, ApiKeyDTO } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Button, Spinner } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/hooks/useApiKeys'
import { notification } from '@/notifications/notification-store'
import { ApiKeyCreateDialog } from './ApiKeyCreateDialog'
import { ApiKeyRevealDialog } from './ApiKeyRevealDialog'
import { ApiKeyTable } from './ApiKeyTable'

/**
 * The API-keys surface: a section of Settings rather than a page of its own, since
 * a key belongs to the account the rest of that page is about.
 *
 * Holds the created key in state only until its reveal dialog closes. It is
 * deliberately not put in the query cache — a cached plaintext would outlive the
 * dialog and be handed to every later reader of the list.
 */
export function ApiKeysSection() {
	const { data: apiKeys, isLoading } = useApiKeys()
	const createKey = useCreateApiKey()
	const revokeKey = useRevokeApiKey()

	const [creating, setCreating] = useState(false)
	const [revealed, setRevealed] = useState<ApiKeyCreatedDTO | null>(null)
	const [pendingRevoke, setPendingRevoke] = useState<ApiKeyDTO | null>(null)

	function handleCreate(input: { name: string; expiresInDays: number }) {
		createKey.mutate(input, {
			onSuccess: (created) => {
				setCreating(false)
				// Straight into the reveal: this is the only moment the plaintext exists.
				setRevealed(created)
				notification.success(`API key "${created.name}" created`)
			},
		})
	}

	function handleConfirmRevoke() {
		if (!pendingRevoke) return
		const target = pendingRevoke
		revokeKey.mutate(target.apiKeyId, {
			onSettled: () => setPendingRevoke(null),
		})
	}

	return (
		<SectionCard
			title="API keys"
			description="Personal keys for the Figma plugin, scripts, and anything else calling ATLAS as you."
		>
			<div className="mb-4 flex justify-end">
				<Button type="button" variant="primary" className="rounded-ds-full px-5" onPress={() => setCreating(true)}>
					Create key
				</Button>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-8">
					<Spinner />
				</div>
			) : (
				<ApiKeyTable
					apiKeys={apiKeys ?? []}
					revokingId={revokeKey.isPending ? (pendingRevoke?.apiKeyId ?? undefined) : undefined}
					onRevoke={setPendingRevoke}
				/>
			)}

			<ApiKeyCreateDialog
				open={creating}
				submitting={createKey.isPending}
				onCreate={handleCreate}
				onCancel={() => setCreating(false)}
			/>

			<ApiKeyRevealDialog
				open={revealed !== null}
				name={revealed?.name ?? ''}
				plaintextKey={revealed?.plaintextKey ?? ''}
				// Drops the plaintext from memory. There is no way back to it.
				onClose={() => setRevealed(null)}
			/>

			<ConfirmDialog
				open={pendingRevoke !== null}
				title="Revoke this API key?"
				description={
					pendingRevoke
						? `"${pendingRevoke.name}" stops working immediately, and this cannot be undone. Anything still using it will start failing.`
						: undefined
				}
				confirmLabel="Revoke"
				confirmVariant="danger"
				busyLabel="Revoking…"
				submitting={revokeKey.isPending}
				onConfirm={handleConfirmRevoke}
				onCancel={() => setPendingRevoke(null)}
			/>
		</SectionCard>
	)
}
